import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Routes, Route, Navigate, NavLink, matchPath, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaSignOutAlt, FaUserCircle, FaRegCalendarAlt, FaRegClock, FaRegCalendarCheck, FaFileInvoiceDollar, FaSuitcase, FaMoneyBillWave, FaReceipt, FaFileAlt, FaRegCommentDots, FaFileSignature, FaEllipsisH, FaTimes, FaUsers, FaAddressBook } from 'react-icons/fa';

import HeaderCard from '../../components/Header/HeaderCard';
import Cloche from '../../components/Navigation/Cloche';
import LanguageSwitcher from '../../components/Header/LanguageSwitcher';
import BoutonInstallerApplication from '../../components/Header/BoutonInstallerApplication';
import MesInformations from './MesInformations';
import MesConges from './MesConges';
import Pointage from './Pointage';
import MaFeuilleDeTemps from './MaFeuilleDeTemps';
import MesBulletins from './MesBulletins';
import MesOrdresMission from './MesOrdresMission';
import MesAvances from './MesAvances';
import MesNotesFrais from './MesNotesFrais';
import MesAttestations from './MesAttestations';
import MesAutresDemandes from './MesAutresDemandes';
import MonContrat from './MonContrat';
import MonEquipe from './MonEquipe';
import Annuaire from '../Annuaire/Annuaire';
import { canValiderHeuresSup, isManager } from '../../components/Auth/permissions';
import { apiService } from '../../components/Config/api.service';

/**
 * Coquille de l'espace collaborateur (Yros/Kavero#314, unit 1 ; navigation
 * Yros/Kavero#328).
 *
 * Coquille **distincte**, pas une coquille interne diminuée : le mobile est le
 * cas nominal, la page se conçoit à 375px puis s'élargit. D'où l'absence de
 * toute largeur fixe en pixels — seules des largeurs relatives et un
 * `max-w-*`. La marge de la coquille interne (`ml-[250px]` / `ml-[70px]`) vit
 * dans `App.js` et n'est pas appliquée à l'employé.
 *
 * **Deux destinations, deux adresses.** Des onglets en état local casseraient
 * le retour du navigateur et interdiraient le lien profond ; ce sont donc de
 * vraies routes imbriquées. Une seule barre de navigation dans le balisage,
 * posée en bas sur mobile (à portée de pouce) et en colonne à partir de `lg` :
 * la dupliquer dupliquerait aussi les repères d'un lecteur d'écran.
 *
 * Chaque destination ne charge que ses propres données — le profil appelle C6,
 * « Mes congés » appelle C10.
 */

const LIEN_BASE =
  'flex flex-1 min-w-0 lg:flex-none flex-col lg:flex-row items-center justify-center ' +
  'lg:justify-start gap-1 lg:gap-3 min-h-[52px] lg:min-h-[44px] px-2 lg:px-3 py-2 ' +
  'rounded-xl text-xs lg:text-sm font-medium text-center lg:text-left transition-colors';

// Les destinations de l'espace, rangées par métier (Yros/Kavero#416). Sur
// téléphone, la barre basse ne garde que les quatre principales et un
// « Plus » : au-delà de cinq entrées, une barre basse défile ou se tasse, ce
// que les règles de navigation mobile écartent. Sur ordinateur, la colonne
// garde tout, sous des titres de section.
const SECTIONS = [
  {
    id: 'accueil',
    destinations: [
      { id: 'profil', to: '/espace', end: true, icone: FaUserCircle, libelle: 'espaceEmploye.navProfil', principale: true },
      // Lu dans la Paie, en lecture seule (Yros/Kavero#12).
      { id: 'contrat', to: '/espace/contrat', icone: FaFileSignature, libelle: 'espaceEmploye.navContrat', principale: false },
      // Yros/Kavero#9, ouverte à tous par #488 : le N+1 pour chacun, les N-1
      // et leurs demandes pour le manager.
      { id: 'equipe', to: '/espace/equipe', icone: FaUsers, libelle: 'espaceEmploye.navEquipe', principale: false },
      // Yros/Kavero#199 : l'annuaire interne, pour tout salarié.
      { id: 'annuaire', to: '/espace/annuaire', icone: FaAddressBook, libelle: 'espaceEmploye.navAnnuaire', principale: false },
    ],
  },
  {
    id: 'temps',
    titre: 'espaceEmploye.sections.temps',
    destinations: [
      { id: 'conges', to: '/espace/conges', icone: FaRegCalendarAlt, libelle: 'espaceEmploye.navConges', principale: true },
      { id: 'pointage', to: '/espace/pointage', icone: FaRegClock, libelle: 'espaceEmploye.navPointage', principale: true },
      // Hors des quatre principales de la barre basse : elle passe par « Plus ».
      { id: 'feuille-temps', to: '/espace/feuille-temps', icone: FaRegCalendarCheck, libelle: 'espaceEmploye.navFeuilleTemps', principale: false },
    ],
  },
  {
    id: 'paie',
    titre: 'espaceEmploye.sections.paie',
    destinations: [
      // Pas de `end` : la destination n'a pas de sous-route.
      { id: 'bulletins', to: '/espace/bulletins', icone: FaFileInvoiceDollar, libelle: 'espaceEmploye.navBulletins', principale: true },
    ],
  },
  {
    id: 'demandes',
    titre: 'espaceEmploye.sections.demandes',
    destinations: [
      { id: 'ordres-mission', to: '/espace/ordres-mission', icone: FaSuitcase, libelle: 'espaceEmploye.navOrdresMission', principale: false },
      { id: 'avances', to: '/espace/avances', icone: FaMoneyBillWave, libelle: 'espaceEmploye.navAvances', principale: false },
      { id: 'notes-frais', to: '/espace/notes-frais', icone: FaReceipt, libelle: 'espaceEmploye.navNotesFrais', principale: false },
      { id: 'attestations', to: '/espace/attestations', icone: FaFileAlt, libelle: 'espaceEmploye.navAttestations', principale: false },
      { id: 'autres', to: '/espace/autres', icone: FaRegCommentDots, libelle: 'espaceEmploye.navAutres', principale: false },
    ],
  },
];

// Les destinations réservées au manager (Yros/Kavero#9) ne figurent que pour lui.
const sectionsPour = (manager) => SECTIONS.map((section) => ({
  ...section,
  destinations: section.destinations.filter((d) => !d.manager || manager),
}));

const secondairesDe = (sections) => sections.flatMap((section) => section.destinations)
  .filter((destination) => !destination.principale);

// Le panneau « Plus » suit les sections de la colonne : « Mon contrat » n'est
// pas une demande, il ne se range pas sous « Mes demandes » (Yros/Kavero#12).
const groupesPlusDe = (sections) => sections
  .map((section) => ({ ...section, destinations: section.destinations.filter((d) => !d.principale) }))
  .filter((section) => section.destinations.length > 0);

// L'état courant ne tient pas qu'à la couleur : le fond et la graisse le
// disent aussi, et `aria-current` le dit aux lecteurs d'écran — NavLink le
// pose lui-même.
const lienClasse = ({ isActive }) =>
  `${LIEN_BASE} ${
    isActive
      ? 'bg-[--color-primary] text-white font-semibold'
      : 'text-slate-500 hover:bg-slate-100'
  }`;

function EspaceEmploye({ onLogoutRequest, offreRh = true, user }) {
  const { t } = useTranslation();
  const location = useLocation();
  const [plusOuvert, setPlusOuvert] = useState(false);
  const boutonPlus = useRef(null);
  const panneauPlus = useRef(null);
  const manager = isManager(user);
  // Yros/Kavero#491 : le validateur des heures sup du pointage, manager ou non.
  const validateur = canValiderHeuresSup(user);
  // Demandes de l'équipe en attente du manager (Yros/Kavero#488), et heures sup
  // en attente du validateur : lues au chargement, puis tenues à jour par
  // « Mon équipe » quand il décide.
  const [nbDemandes, setNbDemandes] = useState(0);
  useEffect(() => {
    if ((!manager && !validateur) || !offreRh) return undefined;
    let actif = true;
    const compter = (lire, cle) => (lire()
      .then((reponse) => (reponse?.ok ? (reponse[cle] || []).length : 0))
      .catch(() => 0));
    Promise.all([
      manager ? compter(() => apiService.lireDemandesEquipe(), 'demandes') : 0,
      validateur ? compter(() => apiService.lireHeuresSupPointage(), 'heures_sup') : 0,
    ]).then(([demandes, heuresSup]) => { if (actif) setNbDemandes(demandes + heuresSup); });
    return () => { actif = false; };
  }, [manager, validateur, offreRh]);
  const compteur = (id) => (id === 'equipe' && nbDemandes > 0 ? (
    <span
      data-testid="nav-equipe-compteur"
      aria-label={t('espaceEmploye.demandesEnAttente', { count: nbDemandes })}
      className="min-w-[18px] px-1.5 py-0.5 rounded-full bg-red-600 text-white text-[11px] leading-none font-semibold"
    >
      {nbDemandes}
    </span>
  ) : null);
  const SECTIONS_VISIBLES = useMemo(() => sectionsPour(manager), [manager]);
  const SECONDAIRES = useMemo(() => secondairesDe(SECTIONS_VISIBLES), [SECTIONS_VISIBLES]);
  const GROUPES_PLUS = useMemo(() => groupesPlusDe(SECTIONS_VISIBLES), [SECTIONS_VISIBLES]);

  // « Plus » se signale quand la page courante est l'une des siennes : sinon
  // la barre basse ne dirait plus où l'on est.
  const plusActif = SECONDAIRES.some((destination) =>
    matchPath({ path: destination.to, end: false }, location.pathname));

  // Une navigation referme le panneau.
  useEffect(() => {
    setPlusOuvert(false);
  }, [location.pathname]);

  // Panneau ouvert : le focus y entre, Échap le referme et le rend au bouton.
  useEffect(() => {
    if (!plusOuvert) return undefined;
    panneauPlus.current?.querySelector('a')?.focus();
    const surTouche = (event) => {
      if (event.key === 'Escape') {
        setPlusOuvert(false);
        boutonPlus.current?.focus();
      }
    };
    document.addEventListener('keydown', surTouche);
    return () => document.removeEventListener('keydown', surTouche);
  }, [plusOuvert]);

  const deconnexion = (
    /* Mobile : l'icône seule suffit — le libellé mange une largeur
       précieuse à 375px. Il revient à partir de `sm`. Le nom accessible
       reste porté par aria-label quand le texte est masqué. */
    <button
      type="button"
      onClick={onLogoutRequest}
      title={t('espaceEmploye.deconnexion')}
      aria-label={t('espaceEmploye.deconnexion')}
      className="bg-[--color-primary] text-white hover:opacity-90 active:scale-95 transition-all duration-200 shadow-sm rounded-full btn flex items-center px-3 sm:px-4 py-2 text-sm font-medium"
    >
      <FaSignOutAlt className="text-sm sm:mr-2" />
      <span className="hidden sm:inline">{t('espaceEmploye.deconnexion')}</span>
    </button>
  );

  // Société Paie seule (YRH-FOUND-014) : l'espace collaborateur est un ajout
  // du RH. Une page unique, et aucune destination montée — donc aucune route
  // RH appelée, que le serveur refuserait.
  if (!offreRh) {
    return (
      <div className="w-full max-w-4xl mx-auto px-3 sm:px-6 py-4 sm:py-6">
        <HeaderCard
          title={t('espaceEmploye.titre')}
          text={t('espaceEmploye.description')}
          rightActions={
            /* Pas de panneau « Plus » ici : la langue et la déconnexion
               restent dans l'en-tête, à toutes les tailles (Yros/Kavero#206). */
            <div data-testid="espace-entete-actions" className="flex items-center gap-3">
              <LanguageSwitcher />
              {deconnexion}
            </div>
          }
        />
        <p className="mt-6 rounded-2xl border border-slate-100 bg-white p-6 text-slate-700 shadow-sm">
          {t('espaceEmploye.horsOffre')}
        </p>
      </div>
    );
  }

  return (
    <div className="w-full max-w-4xl mx-auto px-3 sm:px-6 py-4 sm:py-6">
      <HeaderCard
        title={t('espaceEmploye.titre')}
        text={t('espaceEmploye.description')}
        rightActions={
          <>
            {/* Sur tous les écrans, téléphone compris : c'est là que les
                notifications comptent (Yros/Kavero#21). */}
            <Cloche user={user} />
            <BoutonInstallerApplication />
            {/* Ordinateur seulement : sur téléphone, la langue et la déconnexion
                vivent dans la section « Compte » du panneau « Plus », à portée de
                pouce (Yros/Kavero#206). La déconnexion est une action rare qui
                fait quitter l'espace : un contour, pas le plein de l'action
                principale, et son libellé toujours écrit. */}
            <div data-testid="espace-entete-actions" className="hidden lg:flex items-center gap-3">
              <LanguageSwitcher />
              <button
                type="button"
                onClick={onLogoutRequest}
                data-testid="espace-deconnexion-entete"
                className="flex items-center gap-2 min-h-[44px] px-4 rounded-full border border-slate-300 bg-white text-sm font-medium text-slate-700
                           hover:border-red-300 hover:bg-red-50 hover:text-red-700 transition-colors
                           focus:outline-none focus:ring-2 focus:ring-[--color-primary]"
              >
                <FaSignOutAlt aria-hidden="true" />
                <span>{t('espaceEmploye.deconnexion')}</span>
              </button>
            </div>
          </>
        }
      />

      <div className="lg:flex lg:items-start lg:gap-6">
        {/* Voile du panneau « Plus » : sous la barre, qui reste lisible, et
            au-dessus de la page, qu'un toucher referme. */}
        {plusOuvert && (
          <button
            type="button"
            tabIndex={-1}
            aria-hidden="true"
            onClick={() => setPlusOuvert(false)}
            className="lg:hidden fixed inset-0 z-30 bg-slate-900/30 cursor-default"
          />
        )}
        <nav
          aria-label={t('espaceEmploye.navigation')}
          // Mobile : barre basse fixe, à portée de pouce et dégagée de la
          // zone de geste système. Desktop : colonne dans le flux, collante.
          className="fixed bottom-0 inset-x-0 z-40 flex bg-white border-t border-slate-200
                     px-2 pt-1
                     lg:static lg:z-auto lg:w-52 lg:shrink-0 lg:flex-col lg:gap-1 lg:p-2
                     lg:border lg:border-slate-100 lg:rounded-2xl lg:shadow-sm
                     lg:sticky lg:top-6"
          style={{ paddingBottom: 'max(0.25rem, env(safe-area-inset-bottom))' }}
        >
          {SECTIONS_VISIBLES.map((section) => (
            <React.Fragment key={section.id}>
              {section.titre && (
                <p className="hidden lg:block mt-2 border-t border-slate-100 px-3 pt-4 pb-1.5 text-xs font-bold uppercase tracking-wider text-[--color-primary]">
                  {t(section.titre)}
                </p>
              )}
              {section.destinations.map(({ id, to, end, icone: Icone, libelle, principale }) => (
                <NavLink
                  key={id}
                  to={to}
                  end={end}
                  data-testid={`nav-espace-${id}`}
                  data-principale={String(principale)}
                  className={(etat) => `${lienClasse(etat)}${principale ? '' : ' hidden lg:flex'}`}
                >
                  <Icone className="w-5 h-5 shrink-0" aria-hidden="true" />
                  <span>{t(libelle)}</span>
                  {compteur(id)}
                </NavLink>
              ))}
            </React.Fragment>
          ))}

          {/* Téléphone seulement : les destinations secondaires, dans un
              panneau posé sur la barre, et non dans une seconde navigation. */}
          <button
            ref={boutonPlus}
            type="button"
            data-testid="nav-espace-plus"
            aria-expanded={plusOuvert}
            aria-controls="espace-plus-panneau"
            data-actif={String(plusActif)}
            onClick={() => setPlusOuvert((ouvert) => !ouvert)}
            className={`${LIEN_BASE} lg:hidden ${
              plusActif ? 'bg-[--color-primary] text-white font-semibold' : 'text-slate-500 hover:bg-slate-100'
            }`}
          >
            <span className="relative">
              <FaEllipsisH className="w-5 h-5 shrink-0" aria-hidden="true" />
              {/* « Mon équipe » vit dans « Plus » sur téléphone : le point y signale les demandes. */}
              {nbDemandes > 0 && (
                <span data-testid="nav-plus-pastille" aria-hidden="true"
                  className="absolute -top-1 -right-1.5 w-2 h-2 rounded-full bg-red-600" />
              )}
            </span>
            <span>{t('espaceEmploye.navPlus')}</span>
          </button>

          {plusOuvert && (
            <>
              <div
                id="espace-plus-panneau"
                ref={panneauPlus}
                data-testid="espace-plus-panneau"
                role="group"
                aria-label={t('espaceEmploye.plusTitre')}
                className="lg:hidden absolute bottom-full inset-x-0 bg-white border-t border-slate-200 rounded-t-2xl shadow-lg px-3 pt-3 pb-2"
              >
                <div className="flex items-center justify-end">
                  <button
                    type="button"
                    onClick={() => {
                      setPlusOuvert(false);
                      boutonPlus.current?.focus();
                    }}
                    aria-label={t('espaceEmploye.fermerPlus')}
                    className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-[--color-primary]"
                  >
                    <FaTimes aria-hidden="true" />
                  </button>
                </div>
                {GROUPES_PLUS.map((groupe) => (
                  <div key={groupe.id} data-testid={`espace-plus-groupe-${groupe.id}`}>
                    {groupe.titre && (
                      <p className="pt-2 pb-2 text-xs font-bold uppercase tracking-wider text-[--color-primary]">
                        {t(groupe.titre)}
                      </p>
                    )}
                    <ul className="space-y-1">
                      {groupe.destinations.map(({ id, to, icone: Icone, libelle }) => (
                        <li key={id}>
                          <NavLink
                            to={to}
                            data-testid={`nav-espace-plus-${id}`}
                            onClick={() => setPlusOuvert(false)}
                            className={({ isActive }) =>
                              `flex items-center gap-3 min-h-[44px] px-3 py-2 rounded-xl text-sm font-medium transition-colors ${
                                isActive ? 'bg-[--color-primary] text-white font-semibold' : 'text-slate-700 hover:bg-slate-100'
                              }`}
                          >
                            <Icone className="w-5 h-5 shrink-0" aria-hidden="true" />
                            <span>{t(libelle)}</span>
                            {compteur(id)}
                          </NavLink>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}

                {/* Compte : ce que l'en-tête porte sur ordinateur. La
                    déconnexion ferme le panneau puis passe par `LogoutModal`,
                    qui confirme — aucun second chemin de sortie. */}
                <div
                  role="group"
                  aria-labelledby="espace-plus-compte"
                  data-testid="espace-plus-compte"
                  className="mt-3 border-t border-slate-100 pt-3"
                >
                  <p id="espace-plus-compte" className="pb-2 text-xs font-bold uppercase tracking-wider text-[--color-primary]">
                    {t('espaceEmploye.sections.compte')}
                  </p>
                  <div className="flex items-center justify-between gap-3 min-h-[44px] px-3">
                    <span className="text-sm font-medium text-slate-700">{t('espaceEmploye.langue')}</span>
                    <LanguageSwitcher />
                  </div>
                  <button
                    type="button"
                    data-testid="espace-deconnexion-plus"
                    onClick={() => {
                      setPlusOuvert(false);
                      onLogoutRequest();
                    }}
                    className="mt-1 flex w-full items-center gap-3 min-h-[44px] px-3 py-2 rounded-xl text-sm font-medium text-red-600
                               hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-300"
                  >
                    <FaSignOutAlt className="w-5 h-5 shrink-0" aria-hidden="true" />
                    <span>{t('espaceEmploye.deconnexion')}</span>
                  </button>
                </div>
              </div>
            </>
          )}
        </nav>

        {/* La barre basse est fixe : le contenu réserve sa hauteur, sinon sa
            dernière ligne se lit sous elle. */}
        <main className="flex-1 min-w-0 mt-4 sm:mt-6 lg:mt-0 pb-28 lg:pb-0">
          <Routes>
            <Route index element={<MesInformations />} />
            <Route path="conges" element={<MesConges />} />
            <Route path="pointage" element={<Pointage />} />
            <Route path="feuille-temps" element={<MaFeuilleDeTemps />} />
            {/* L'ordre n'y fait rien : `<Routes>` classe par spécificité, pas
                par déclaration (react-router-dom 6.30.3), et `bulletins` l'emporte
                donc sur `path="*"` où qu'elle soit. Elle est placée avant le
                rattrapage pour se lire avec les autres destinations. */}
            <Route path="bulletins" element={<MesBulletins />} />
            <Route path="ordres-mission" element={<MesOrdresMission />} />
            <Route path="avances" element={<MesAvances />} />
            <Route path="notes-frais" element={<MesNotesFrais />} />
            <Route path="attestations" element={<MesAttestations />} />
            <Route path="autres" element={<MesAutresDemandes />} />
            <Route path="contrat" element={<MonContrat />} />
            <Route path="equipe" element={<MonEquipe manager={manager} validateur={validateur} onCompteDemandes={setNbDemandes} />} />
            <Route path="annuaire" element={<Annuaire />} />
            {/* Une adresse périmée sous /espace ramène au profil plutôt que
                d'afficher une coquille vide. */}
            <Route path="*" element={<Navigate to="/espace" replace />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}

export default EspaceEmploye;
